import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import {
  UserPlus,
  Search,
  Phone,
  PhoneOff,
  CheckCircle2,
  XCircle,
  Clock,
  PauseCircle,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  FileText,
  X,
  Mail,
  User as UserIcon,
  Calendar,
  ExternalLink,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { PageLoader } from "@/components/ui/Spinner";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useAuthStore } from "@/store/auth";
import { toDatetimeLocalValue } from "@/utils/datetime";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import {
  useCandidates,
  useCreateCandidate,
  useUpdateCandidate,
  useDeleteCandidate,
  useUploadCandidateFile,
  useDeleteCandidateFile,
  useSendInterviewSms,
  useCandidateSmsInfo,
} from "../hooks/useRecruitment";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  type Candidate,
  type CandidateFile,
  type CandidateStatus,
  type CandidateFileType,
} from "../types";

// ── helpers ──────────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: CandidateStatus }) {
  if (status === "accepted") return <CheckCircle2 size={14} className="text-green-600" />;
  if (status === "rejected") return <XCircle size={14} className="text-red-500" />;
  if (status === "on_hold") return <PauseCircle size={14} className="text-yellow-500" />;
  return <Clock size={14} className="text-[var(--color-primary)]" />;
}

const fmtTime = (d: string | null) =>
  d
    ? new Date(d).toLocaleTimeString("pl-PL", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const isSameLocalDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const EMPTY_FORM: Partial<Candidate> = {
  first_name: "",
  last_name: "",
  position: "",
  candidate_phone: "",
  candidate_email: "",
  phone_contacted: false,
  process_leader: null,
  branch: null,
  task_sent_date: null,
  task_received: false,
  task_feedback: "",
  interview_date: null,
  interview_participants: [],
  confirmation_email_sent: false,
  notes: "",
  availability_notes: "",
  status: "in_process",
  first_work_day: null,
  rate: "",
  hr_notified: false,
  job_posting_url: "",
};

const STATUSES: { value: CandidateStatus; label: string }[] = [
  { value: "in_process", label: "W trakcie" },
  { value: "accepted", label: "Przyjęty" },
  { value: "rejected", label: "Odrzucony" },
  { value: "on_hold", label: "Wstrzymany" },
];

// ── table sorting ─────────────────────────────────────────────────────────────

type SortKey =
  | "candidate"
  | "process_leader"
  | "phone"
  | "task"
  | "interview"
  | "status"
  | "files";
type SortDir = "asc" | "desc";

// Progress order for the status column so sorting follows the recruitment
// funnel rather than the alphabet.
const STATUS_ORDER: Record<CandidateStatus, number> = {
  in_process: 0,
  on_hold: 1,
  accepted: 2,
  rejected: 3,
};

// Comparable value for a candidate under a given sort key. Returns "" (or null)
// for missing values, which the comparator always pushes to the bottom. Numeric
// zeros (no files, phone not contacted) are real values, never treated as empty.
function sortValue(c: Candidate, key: SortKey): string | number | null {
  switch (key) {
    case "candidate":
      return `${c.last_name} ${c.first_name}`.trim().toLowerCase();
    case "process_leader":
      return c.process_leader_name?.toLowerCase() ?? "";
    case "phone":
      return c.phone_contacted ? 1 : 0;
    case "task":
      // received > sent (awaiting) > not sent
      return c.task_received ? 2 : c.task_sent_date ? 1 : 0;
    case "interview":
      return c.interview_date ?? "";
    case "status":
      return STATUS_ORDER[c.status];
    case "files":
      return c.files.length;
  }
}

function compareCandidates(a: Candidate, b: Candidate, key: SortKey, dir: SortDir): number {
  const va = sortValue(a, key);
  const vb = sortValue(b, key);
  const aEmpty = va === "" || va === null;
  const bEmpty = vb === "" || vb === null;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // empties always last, regardless of direction
  if (bEmpty) return -1;
  const factor = dir === "asc" ? 1 : -1;
  if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
  return String(va).localeCompare(String(vb), "pl") * factor;
}

// Preview of the interview-confirmation SMS. Mirrors the authoritative
// server-side template in backend/apps/recruitment/sms.py — keep both in sync.
// Date/time/position are filled from the current form; the "(stanowisko …)"
// clause is omitted when no position is set.
function buildInterviewSmsPreview(form: Partial<Candidate>): string {
  const dt = form.interview_date ? new Date(form.interview_date) : null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const dateStr = dt
    ? `${pad(dt.getDate())}.${pad(dt.getMonth() + 1)}.${dt.getFullYear()}`
    : "";
  const timeStr = dt ? `${pad(dt.getHours())}:${pad(dt.getMinutes())}` : "";
  const position = (form.position ?? "").trim();
  const positionClause = position ? ` (stanowisko ${position})` : "";

  return (
    "Dzień dobry,\n\n" +
    "Zgodnie z rozmową telefoniczną zapraszam Pana/Panią na rozmowę o pracę" +
    `${positionClause}, która odbędzie się ${dateStr} o godzinie ${timeStr}.\n\n` +
    "Siedziba firmy znajduje się:\n" +
    "Wrocław\n" +
    "Millennium Tower IV (wejście od ul. Strzegomskiej - budynek z czerwonym napisem MERCK)\n" +
    "ul. Strzegomska 42 AB/8\n" +
    "53-611 Wrocław\n\n" +
    "Tutaj podsyłam również pinezkę z lokalizacją darmowego parkingu przy ul. Robotniczej: " +
    "https://maps.app.goo.gl/GacB2t74qAfhsQ1v6\n\n" +
    "Do zobaczenia."
  );
}

// ── shared input class ────────────────────────────────────────────────────────

const inputCls =
  "block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]";

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

// ── file upload zone ──────────────────────────────────────────────────────────

interface FileUploadZoneHandle {
  commit: () => Promise<void>;
}

const FileUploadZone = forwardRef<
  FileUploadZoneHandle,
  {
    candidateId: string;
    fileType: CandidateFileType;
    existingFiles: CandidateFile[];
    singleFile?: boolean;
  }
>(function FileUploadZone({ candidateId, fileType, existingFiles, singleFile }, ref) {
  const upload = useUploadCandidateFile();
  const deleteMutation = useDeleteCandidateFile();

  // Server files already saved; managed locally so deletes are reflected immediately
  const [serverFiles] = useState<CandidateFile[]>(() =>
    existingFiles.filter((f) => f.file_type === fileType)
  );
  // IDs of server files the user marked for deletion (applied on commit)
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  // Local File objects waiting to be uploaded on commit
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  useImperativeHandle(ref, () => ({
    commit: async () => {
      for (const id of deletedIds) {
        await deleteMutation.mutateAsync({ candidateId, fileId: id });
      }
      for (const file of pendingFiles) {
        await upload.mutateAsync({ candidateId, file, fileType });
      }
    },
  }));

  // Receives the FULL new pending list from FileDropZone (add + remove alike).
  // In singleFile mode a pending file supersedes server files; removing it
  // restores them.
  const handlePendingChange = (next: File[]) => {
    setPendingFiles(next);
    if (singleFile) {
      setDeletedIds(next.length > 0 ? new Set(serverFiles.map((f) => f.id)) : new Set());
    }
  };

  const removeServerFile = (id: string) => {
    setDeletedIds((prev) => new Set([...prev, id]));
  };

  const visibleServerFiles = serverFiles.filter((f) => !deletedIds.has(f.id));

  return (
    <div className="space-y-2">
      <FileDropZone
        files={pendingFiles}
        onFilesChange={handlePendingChange}
        multiple={!singleFile}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.dxf,.dwg,.dgn,.qgz,.qgs,.gpkg,.shp,.geojson,.kml,.kmz,.svg"
      />

      {/* Server files already saved */}
      {visibleServerFiles.length > 0 && (
        <div className="space-y-1 pt-1">
          {visibleServerFiles.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 rounded-md px-3 py-2"
            >
              <FileText size={13} className="text-gray-400 flex-shrink-0" />
              <a
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                title={f.original_name}
                className="text-sm text-gray-700 dark:text-gray-300 hover:text-[var(--color-primary)] hover:underline flex-1 truncate"
              >
                {f.original_name}
              </a>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 flex-shrink-0">
                zapisany
              </span>
              <button
                onClick={() => removeServerFile(f.id)}
                className="p-0.5 rounded text-gray-400 hover:text-red-600 flex-shrink-0"
                title="Usuń plik"
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ── candidate form modal ──────────────────────────────────────────────────────

type Section = "basic" | "task" | "interview" | "decision";

function CandidateModal({
  open,
  onClose,
  initial,
  users,
}: {
  open: boolean;
  onClose: () => void;
  initial: Candidate | null;
  users: { id: string; first_name: string; last_name: string }[];
}) {
  const isEdit = !!initial;
  const create = useCreateCandidate();
  const update = useUpdateCandidate();
  const sendSmsMutation = useSendInterviewSms();
  const { data: smsInfo } = useCandidateSmsInfo(open && initial ? initial.id : undefined);
  const currentUser = useAuthStore((s) => s.user);
  const cvZoneRef = useRef<FileUploadZoneHandle>(null);
  const taskZoneRef = useRef<FileUploadZoneHandle>(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const { data: branches = [] } = useQuery({
    queryKey: ["branches-list"],
    queryFn: () => apiClient.get<{ data: { id: string; name: string }[] }>("/auth/branches/").then((r) => r.data.data),
    staleTime: 1000 * 60 * 10,
  });

  const [form, setForm] = useState<Partial<Candidate>>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [section, setSection] = useState<Section>("basic");
  // Opt-in to send the interview-confirmation SMS on save. Local state only —
  // never persisted, and reset every time the modal (re)opens.
  const [sendSms, setSendSms] = useState(false);
  // Edited SMS body. While `smsEdited` is false the body tracks the composed
  // template (so date/position edits flow through); once the recruiter types,
  // it freezes on their text until they reset it.
  const [smsMessage, setSmsMessage] = useState("");
  const [smsEdited, setSmsEdited] = useState(false);

  useEffect(() => {
    if (open) {
      const base = initial ?? {
        ...EMPTY_FORM,
        interview_participants: currentUser ? [currentUser.id] : [],
        branch: (currentUser?.branch as unknown as { id: string } | null)?.id ?? null,
      };
      setForm(base);
      setErrors({});
      setSection("basic");
      setSendSms(false);
      setSmsMessage("");
      setSmsEdited(false);
    }
  }, [open, initial]);

  // The message shown/sent: the recruiter's edit if any, else the live template.
  const smsDefaultBody = buildInterviewSmsPreview(form);
  const smsBody = smsEdited ? smsMessage : smsDefaultBody;

  const set = (field: keyof Candidate, value: unknown) =>
    setForm((f) => ({ ...f, [field]: value }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.first_name?.trim()) e.first_name = "Wymagane";
    if (!form.last_name?.trim()) e.last_name = "Wymagane";
    if (!form.candidate_phone?.trim()) e.candidate_phone = "Wymagane";
    return e;
  };

  const parseErrors = (err: unknown, fallback: string): string => {
    const resp = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
    return resp && typeof resp === "object"
      ? Object.values(resp as Record<string, string[]>).flat().join(" | ")
      : typeof resp === "string"
      ? resp
      : fallback;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    // Guard the SMS opt-in before saving so we never silently skip the send.
    if (sendSms) {
      if (!form.candidate_phone?.trim()) {
        setErrors({
          submit:
            "Nie można wysłać SMS: brak numeru telefonu kandydata. Uzupełnij numer w zakładce „Podstawowe” lub odznacz wysyłkę SMS.",
        });
        setSection("basic");
        return;
      }
      if (!smsBody.trim()) {
        setErrors({
          submit: "Nie można wysłać SMS: treść wiadomości jest pusta.",
        });
        setSection("interview");
        return;
      }
      // Only the default (un-edited) template needs the date to render properly.
      if (!smsEdited && !form.interview_date) {
        setErrors({
          submit:
            "Nie można wysłać SMS: uzupełnij datę i godzinę rozmowy, wpisz własną treść lub odznacz wysyłkę SMS.",
        });
        setSection("interview");
        return;
      }
    }

    const payload: Partial<Candidate> = {
      ...form,
      task_sent_date: form.task_sent_date || null,
      interview_date: form.interview_date || null,
      first_work_day: form.first_work_day || null,
      process_leader: form.process_leader || null,
    };

    try {
      let candidateId: string;
      if (isEdit && initial) {
        setIsCommitting(true);
        try {
          await cvZoneRef.current?.commit();
          await taskZoneRef.current?.commit();
        } catch {
          setErrors({ submit: "Błąd przesyłania plików." });
          setIsCommitting(false);
          return;
        }
        setIsCommitting(false);
        await update.mutateAsync({ id: initial.id, data: payload });
        candidateId = initial.id;
      } else {
        const created = await create.mutateAsync(payload);
        candidateId = created.id;
      }

      // Save succeeded — send the interview-confirmation SMS if opted in.
      if (sendSms) {
        try {
          await sendSmsMutation.mutateAsync({ candidateId, message: smsBody });
        } catch (err) {
          // The record is already saved; surface the SMS failure and keep the
          // modal open so the user knows the message did not go out.
          setErrors({
            submit: `Zapisano dane, ale nie udało się wysłać SMS: ${parseErrors(err, "spróbuj ponownie.")}`,
          });
          return;
        }
      }

      onClose();
    } catch (err) {
      setErrors({ submit: parseErrors(err, "Błąd zapisu.") });
    }
  };

  const isPending =
    create.isPending || update.isPending || isCommitting || sendSmsMutation.isPending;

  const tabs: { key: Section; label: string }[] = [
    { key: "basic", label: "Podstawowe" },
    { key: "task", label: "Zadanie" },
    { key: "interview", label: "Rozmowa" },
    { key: "decision", label: "Decyzja" },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `${initial?.first_name} ${initial?.last_name}` : "Nowy kandydat"}
      size="lg"
    >
      {/* Section tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200 dark:border-gray-700 -mx-6 px-6 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setSection(t.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors whitespace-nowrap ${
              section === t.key
                ? "bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4">

        {/* ── Podstawowe ── */}
        {section === "basic" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Imię" required error={errors.first_name}>
                <input
                  className={inputCls}
                  value={form.first_name ?? ""}
                  onChange={(e) => set("first_name", e.target.value)}
                />
              </Field>
              <Field label="Nazwisko" required error={errors.last_name}>
                <input
                  className={inputCls}
                  value={form.last_name ?? ""}
                  onChange={(e) => set("last_name", e.target.value)}
                />
              </Field>
            </div>

            <Field label="Stanowisko">
              <input
                className={inputCls}
                placeholder="np. Specjalista ds. GIS"
                value={form.position ?? ""}
                onChange={(e) => set("position", e.target.value)}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Telefon kandydata" required error={errors.candidate_phone}>
                <input
                  className={inputCls}
                  type="tel"
                  placeholder="np. +48 600 100 200"
                  value={form.candidate_phone ?? ""}
                  onChange={(e) => set("candidate_phone", e.target.value)}
                />
              </Field>
              <Field label="Email kandydata" error={errors.candidate_email}>
                <input
                  className={inputCls}
                  type="email"
                  placeholder="np. jan.kowalski@gmail.com"
                  value={form.candidate_email ?? ""}
                  onChange={(e) => set("candidate_email", e.target.value)}
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Prowadzący proces">
                <select
                  className={inputCls}
                  value={form.process_leader ?? ""}
                  onChange={(e) => set("process_leader", e.target.value || null)}
                >
                  <option value="">— brak —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Oddział">
                <select
                  className={inputCls}
                  value={form.branch ?? ""}
                  onChange={(e) => set("branch", e.target.value || null)}
                >
                  <option value="">— brak —</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Status">
                <select
                  className={inputCls}
                  value={form.status ?? "in_process"}
                  onChange={(e) => set("status", e.target.value as CandidateStatus)}
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Dyspozycyjność / uwagi wstępne">
                <input
                  className={inputCls}
                  placeholder="np. brak okresu wypowiedzenia..."
                  value={form.availability_notes ?? ""}
                  onChange={(e) => set("availability_notes", e.target.value)}
                />
              </Field>
            </div>

            <Field label="Link do ogłoszenia">
              <input
                className={inputCls}
                type="url"
                placeholder="np. https://pracuj.pl/..."
                value={form.job_posting_url ?? ""}
                onChange={(e) => set("job_posting_url", e.target.value)}
              />
            </Field>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.phone_contacted ?? false}
                onChange={(e) => set("phone_contacted", e.target.checked)}
                className="rounded"
              />
              Kontakt telefoniczny nawiązany
            </label>

            {/* CV files — only in edit mode (need candidateId) */}
            {isEdit && initial && (
              <Field label="CV">
                <FileUploadZone
                  ref={cvZoneRef}
                  candidateId={initial.id}
                  fileType="cv"
                  existingFiles={initial.files}
                  singleFile
                />
              </Field>
            )}
            {!isEdit && (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Pliki CV można dodać po zapisaniu kandydata.
              </p>
            )}
          </>
        )}

        {/* ── Zadanie ── */}
        {section === "task" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Data przesłania zadania">
                <DatePicker
                  value={form.task_sent_date ?? ""}
                  onChange={(v) => set("task_sent_date", v || null)}
                />
              </Field>
              <div className="flex items-end pb-0.5">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.task_received ?? false}
                    onChange={(e) => set("task_received", e.target.checked)}
                    className="rounded"
                  />
                  Otrzymano zrobione zadanie
                </label>
              </div>
            </div>

            <Field label="Feedback z zadania rekrutacyjnego">
              <textarea
                rows={6}
                className={inputCls}
                placeholder="Szczegółowy feedback z oceny zadania..."
                value={form.task_feedback ?? ""}
                onChange={(e) => set("task_feedback", e.target.value)}
              />
            </Field>

            {isEdit && initial && (
              <Field label="Pliki zadania">
                <FileUploadZone
                  ref={taskZoneRef}
                  candidateId={initial.id}
                  fileType="task"
                  existingFiles={initial.files}
                />
              </Field>
            )}
            {!isEdit && (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Pliki zadania można dodać po zapisaniu kandydata.
              </p>
            )}
          </>
        )}

        {/* ── Rozmowa ── */}
        {section === "interview" && (
          <>
            <Field label="Data i godzina rozmowy">
              <input
                className={inputCls}
                type="datetime-local"
                value={toDatetimeLocalValue(form.interview_date)}
                onChange={(e) => set("interview_date", e.target.value || null)}
              />
            </Field>

            <Field label="Uczestnicy ze strony GK ELEKTIM">
              <div className="flex flex-wrap gap-2 mt-1">
                {users.map((u) => {
                  const selected = (form.interview_participants ?? []).includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        const current = form.interview_participants ?? [];
                        set(
                          "interview_participants",
                          selected ? current.filter((id) => id !== u.id) : [...current, u.id]
                        );
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        selected
                          ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                          : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[var(--color-primary)]"
                      }`}
                    >
                      {u.first_name} {u.last_name}
                    </button>
                  );
                })}
              </div>
            </Field>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.confirmation_email_sent ?? false}
                onChange={(e) => set("confirmation_email_sent", e.target.checked)}
                className="rounded"
              />
              Wysłano maila potwierdzającego spotkanie
            </label>

            {/* SMS confirmation opt-in — not persisted; sent on save. */}
            <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  className="rounded mt-0.5"
                />
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={14} className="text-[var(--color-primary)]" />
                  Wyślij kandydatowi SMS z potwierdzeniem rozmowy
                </span>
              </label>

              {sendSms && !form.candidate_phone?.trim() && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  Brak numeru telefonu kandydata. Uzupełnij pole „Telefon kandydata” w zakładce
                  „Podstawowe”, aby wysłać SMS.
                </p>
              )}

              {sendSms && !!form.candidate_phone?.trim() && (
                <div className="space-y-1.5">
                  {!smsEdited && !form.interview_date && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Uzupełnij datę i godzinę rozmowy powyżej — zostaną automatycznie wstawione do
                      treści — albo wpisz własną treść poniżej.
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      SMS zostanie wysłany na numer{" "}
                      <strong className="text-gray-700 dark:text-gray-300">
                        {form.candidate_phone}
                      </strong>{" "}
                      po zapisaniu. Treść (edytowalna):
                    </p>
                    {smsEdited && (
                      <button
                        type="button"
                        onClick={() => {
                          setSmsEdited(false);
                          setSmsMessage("");
                        }}
                        className="text-xs text-[var(--color-primary)] hover:underline whitespace-nowrap"
                      >
                        Przywróć domyślną treść
                      </button>
                    )}
                  </div>
                  <textarea
                    rows={12}
                    className={`${inputCls} font-sans leading-relaxed`}
                    value={smsBody}
                    onChange={(e) => {
                      setSmsMessage(e.target.value);
                      setSmsEdited(true);
                    }}
                  />
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 text-right">
                    {smsBody.length} znaków
                  </p>
                </div>
              )}
            </div>

            {/* SMS history + auto-reminder schedule (existing candidates only). */}
            {isEdit && smsInfo && (
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <MessageSquare size={14} className="text-[var(--color-primary)]" />
                  SMS-y do kandydata
                </p>

                {smsInfo.messages.length === 0 ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                    Nie wysłano jeszcze żadnego SMS-a do kandydata.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {smsInfo.messages.map((m) => (
                      <li key={m.id} className="text-xs text-gray-600 dark:text-gray-300">
                        <details>
                          <summary className="cursor-pointer list-none flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-medium">{m.kind_display}</span>
                            <span className="text-gray-500 dark:text-gray-400">
                              {formatDateTime(m.sent_at)}
                            </span>
                            {m.is_automatic ? (
                              <Badge color="blue">wysłano automatycznie</Badge>
                            ) : (
                              <Badge color="gray">
                                ręcznie{m.sent_by_name ? `: ${m.sent_by_name}` : ""}
                              </Badge>
                            )}
                          </summary>
                          <pre className="mt-1.5 whitespace-pre-wrap font-sans text-[11px] text-gray-500 dark:text-gray-400 border-l-2 border-gray-200 dark:border-gray-600 pl-2">
                            {m.message}
                          </pre>
                        </details>
                      </li>
                    ))}
                  </ul>
                )}

                {smsInfo.planned_reminder_at && (
                  <p className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-400">
                    <Clock size={12} />
                    Zaplanowane automatyczne przypomnienie SMS: {formatDateTime(smsInfo.planned_reminder_at)}
                  </p>
                )}
                {!smsInfo.sms_enabled && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">
                    Wysyłka SMS jest obecnie wyłączona w systemie — automatyczne przypomnienia nie
                    są planowane.
                  </p>
                )}
              </div>
            )}

            <Field label="Wnioski / uwagi dot. kandydata">
              <textarea
                rows={6}
                className={inputCls}
                placeholder="Wnioski po rozmowie, mocne/słabe strony, pytania na rozmowę..."
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
          </>
        )}

        {/* ── Decyzja ── */}
        {section === "decision" && (
          <>
            <Field label="Status rekrutacji">
              <select
                className={inputCls}
                value={form.status ?? "in_process"}
                onChange={(e) => set("status", e.target.value as CandidateStatus)}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Pierwszy dzień pracy">
                <DatePicker
                  value={form.first_work_day ?? ""}
                  onChange={(v) => set("first_work_day", v || null)}
                />
              </Field>
              <Field label="Stawka">
                <input
                  className={inputCls}
                  placeholder="np. 5000 netto UZ"
                  value={form.rate ?? ""}
                  onChange={(e) => set("rate", e.target.value)}
                />
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.hr_notified ?? false}
                onChange={(e) => set("hr_notified", e.target.checked)}
                className="rounded"
              />
              Info wysłane do kadr
            </label>
          </>
        )}

        {errors.submit && <p className="text-sm text-red-600">{errors.submit}</p>}

        <div className="flex gap-3 pt-1 border-t border-gray-100 dark:border-gray-700">
          <Button loading={isPending} onClick={submit}>
            {sendSms
              ? isEdit
                ? "Zapisz i wyślij SMS"
                : "Dodaj i wyślij SMS"
              : isEdit
              ? "Zapisz zmiany"
              : "Dodaj kandydata"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── candidate row ─────────────────────────────────────────────────────────────

function CandidateRow({
  c,
  onEdit,
  onDelete,
}: {
  c: Candidate;
  onEdit: (c: Candidate) => void;
  onDelete: (c: Candidate) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const deleteFile = useDeleteCandidateFile();

  const cvFiles = c.files.filter((f) => f.file_type === "cv");
  const taskFiles = c.files.filter((f) => f.file_type === "task");
  const otherFiles = c.files.filter((f) => f.file_type === "other");

  return (
    <>
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
        {/* Kandydat */}
        <td className="px-4 py-3">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {c.first_name} {c.last_name}
          </div>
          {c.position && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {c.position}
            </div>
          )}
          {c.candidate_phone && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {c.candidate_phone}
            </div>
          )}
          {c.availability_notes && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-[220px]">
              {c.availability_notes}
            </div>
          )}
        </td>

        {/* Prowadzący */}
        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
          {c.process_leader_name ?? "—"}
        </td>

        {/* Tel */}
        <td className="px-3 py-3 text-center">
          {c.phone_contacted ? (
            <Phone size={14} className="text-green-500 mx-auto" />
          ) : (
            <PhoneOff size={14} className="text-gray-300 dark:text-gray-600 mx-auto" />
          )}
        </td>

        {/* Zadanie */}
        <td className="px-3 py-3 text-center">
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              c.task_received
                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                : c.task_sent_date
                ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                : "text-gray-400 dark:text-gray-600"
            }`}
          >
            {c.task_received ? "TAK" : c.task_sent_date ? `${c.days_since_task_sent}d` : "—"}
          </span>
        </td>

        {/* Rozmowa */}
        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {formatDateTime(c.interview_date)}
        </td>

        {/* Status */}
        <td className="px-3 py-3">
          <Badge color={STATUS_COLORS[c.status]}>
            <StatusIcon status={c.status} />
            <span className="ml-1">{STATUS_LABELS[c.status]}</span>
          </Badge>
        </td>

        {/* Pliki */}
        <td className="px-3 py-3">
          <div className="flex flex-wrap gap-1.5">
            {cvFiles.map((f) => (
              <a
                key={f.id}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline flex items-center gap-0.5"
                title={f.original_name}
              >
                <FileText size={12} /> CV
              </a>
            ))}
            {taskFiles.map((f) => (
              <a
                key={f.id}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-0.5"
                title={f.original_name}
              >
                <FileText size={12} /> Zad.
              </a>
            ))}
            {otherFiles.map((f) => (
              <a
                key={f.id}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:underline flex items-center gap-0.5"
                title={f.original_name}
              >
                <FileText size={12} /> {f.original_name.slice(0, 8)}…
              </a>
            ))}
          </div>
        </td>

        {/* Actions */}
        <td className="px-3 py-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              title="Szczegóły"
            >
              {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
            {c.job_posting_url && (
              <a
                href={c.job_posting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-[var(--color-primary)]"
                title="Otwórz ogłoszenie"
              >
                <ExternalLink size={14} />
              </a>
            )}
            <button
              onClick={() => onEdit(c)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-[var(--color-primary)]"
              title="Edytuj"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => onDelete(c)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-red-600"
              title="Usuń"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded detail */}
      {expanded && (
        <tr className="bg-gray-50 dark:bg-gray-800/40">
          <td colSpan={8} className="px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
              {/* Feedback */}
              {c.task_feedback && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    Feedback z zadania
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {c.task_feedback}
                  </p>
                </div>
              )}

              {/* Notes */}
              {c.notes && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    Wnioski / uwagi
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {c.notes}
                  </p>
                </div>
              )}

              {/* Participants */}
              {c.interview_participants_names && c.interview_participants_names.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    Uczestnicy rozmowy
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.interview_participants_names.map((p) => (
                      <span
                        key={p.id}
                        className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)]"
                      >
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Kontakt kandydata */}
              {(c.candidate_phone || c.candidate_email) && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    Kontakt kandydata
                  </p>
                  <div className="space-y-0.5 text-gray-700 dark:text-gray-300">
                    {c.candidate_phone && (
                      <p>
                        Tel:{" "}
                        <a
                          href={`tel:${c.candidate_phone}`}
                          className="text-[var(--color-primary)] hover:underline"
                        >
                          {c.candidate_phone}
                        </a>
                      </p>
                    )}
                    {c.candidate_email && (
                      <p>
                        Email:{" "}
                        <a
                          href={`mailto:${c.candidate_email}`}
                          className="text-[var(--color-primary)] hover:underline"
                        >
                          {c.candidate_email}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Checklist */}
              <div>
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                  Checklist
                </p>
                <ul className="space-y-1.5 text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2">
                    {c.confirmation_email_sent ? (
                      <CheckCircle2 size={13} className="text-green-500" />
                    ) : (
                      <XCircle size={13} className="text-gray-300 dark:text-gray-600" />
                    )}
                    Mail potwierdzający spotkanie
                  </li>
                  <li className="flex items-center gap-2">
                    {c.hr_notified ? (
                      <CheckCircle2 size={13} className="text-green-500" />
                    ) : (
                      <XCircle size={13} className="text-gray-300 dark:text-gray-600" />
                    )}
                    Info do kadr
                  </li>
                </ul>
              </div>

              {/* Decyzja */}
              {(c.first_work_day || c.rate) && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    Zatrudnienie
                  </p>
                  {c.first_work_day && (
                    <p className="text-gray-700 dark:text-gray-300">
                      Pierwszy dzień:{" "}
                      <strong className="font-semibold">{formatDate(c.first_work_day)}</strong>
                    </p>
                  )}
                  {c.rate && (
                    <p className="text-gray-700 dark:text-gray-300 mt-0.5">
                      Stawka: <strong className="font-semibold">{c.rate}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* CV */}
              {cvFiles.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    CV
                  </p>
                  <div className="flex flex-col gap-1">
                    {cvFiles.map((f) => (
                      <div key={f.id} className="flex items-center gap-1.5 bg-white dark:bg-gray-700 rounded-md px-2.5 py-1.5">
                        <FileText size={13} className="text-[var(--color-primary)] flex-shrink-0" />
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-[var(--color-primary)] hover:underline flex-1 truncate">
                          {f.original_name}
                        </a>
                        <button onClick={() => deleteFile.mutate({ candidateId: c.id, fileId: f.id })}
                          className="ml-1 p-0.5 text-gray-300 hover:text-red-500 flex-shrink-0" title="Usuń plik">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pliki zadania */}
              {taskFiles.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                    Pliki zadania
                  </p>
                  <div className="flex flex-col gap-1">
                    {taskFiles.map((f) => (
                      <div key={f.id} className="flex items-center gap-1.5 bg-white dark:bg-gray-700 rounded-md px-2.5 py-1.5">
                        <FileText size={13} className="text-purple-400 flex-shrink-0" />
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-[var(--color-primary)] hover:underline flex-1 truncate">
                          {f.original_name}
                        </a>
                        <button onClick={() => deleteFile.mutate({ candidateId: c.id, fileId: f.id })}
                          className="ml-1 p-0.5 text-gray-300 hover:text-red-500 flex-shrink-0" title="Usuń plik">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── mobile candidate card ─────────────────────────────────────────────────────

function CandidateCard({
  c,
  onEdit,
  onDelete,
}: {
  c: Candidate;
  onEdit: (c: Candidate) => void;
  onDelete: (c: Candidate) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const deleteFile = useDeleteCandidateFile();

  const cvFiles = c.files.filter((f) => f.file_type === "cv");
  const taskFiles = c.files.filter((f) => f.file_type === "task");

  const taskBadge = c.task_received
    ? { text: "Zadanie: TAK", cls: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" }
    : c.task_sent_date
    ? { text: `Zadanie: ${c.days_since_task_sent}d`, cls: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400" }
    : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm overflow-hidden">
      {/* Top row */}
      <div className="px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="min-w-0 flex-1 text-left"
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
              {c.first_name} {c.last_name}
            </div>
            {c.position && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {c.position}
              </div>
            )}
            {c.process_leader_name && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {c.process_leader_name}
              </div>
            )}
          </button>
          <Badge color={STATUS_COLORS[c.status]}>
            <StatusIcon status={c.status} />
            <span className="ml-1">{STATUS_LABELS[c.status]}</span>
          </Badge>
        </div>

        {/* Indicator row */}
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <span
            className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${
              c.phone_contacted
                ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
            }`}
          >
            {c.phone_contacted ? <Phone size={11} /> : <PhoneOff size={11} />}
            {c.phone_contacted ? "Tel ✓" : "Tel"}
          </span>
          {taskBadge && (
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${taskBadge.cls}`}>
              {taskBadge.text}
            </span>
          )}
          {c.interview_date && (
            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)]">
              <Calendar size={11} />
              {formatDateTime(c.interview_date)}
            </span>
          )}
          {c.files.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <FileText size={11} />
              {c.files.length}
            </span>
          )}
        </div>

        {/* Quick actions */}
        <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {expanded ? "Zwiń" : "Szczegóły"}
          </button>
          <div className="flex items-center gap-1">
            {c.candidate_phone && (
              <a
                href={`tel:${c.candidate_phone}`}
                className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Zadzwoń"
              >
                <Phone size={14} />
              </a>
            )}
            {c.candidate_email && (
              <a
                href={`mailto:${c.candidate_email}`}
                className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Email"
              >
                <Mail size={14} />
              </a>
            )}
            {c.job_posting_url && (
              <a
                href={c.job_posting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Otwórz ogłoszenie"
              >
                <ExternalLink size={14} />
              </a>
            )}
            <button
              onClick={() => onEdit(c)}
              className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Edytuj"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => onDelete(c)}
              className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Usuń"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="bg-gray-50 dark:bg-gray-800/60 px-3 py-3 border-t border-gray-100 dark:border-gray-700 space-y-3 text-sm">
          {(c.candidate_phone || c.candidate_email || c.availability_notes) && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Kontakt
              </p>
              <div className="space-y-0.5 text-xs text-gray-700 dark:text-gray-300">
                {c.candidate_phone && (
                  <p>
                    Tel:{" "}
                    <a
                      href={`tel:${c.candidate_phone}`}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      {c.candidate_phone}
                    </a>
                  </p>
                )}
                {c.candidate_email && (
                  <p className="break-all">
                    Email:{" "}
                    <a
                      href={`mailto:${c.candidate_email}`}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      {c.candidate_email}
                    </a>
                  </p>
                )}
                {c.availability_notes && (
                  <p className="text-gray-500 dark:text-gray-400 italic">
                    {c.availability_notes}
                  </p>
                )}
              </div>
            </div>
          )}

          {c.task_feedback && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Feedback z zadania
              </p>
              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {c.task_feedback}
              </p>
            </div>
          )}

          {c.notes && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Wnioski / uwagi
              </p>
              <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {c.notes}
              </p>
            </div>
          )}

          {c.interview_participants_names && c.interview_participants_names.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Uczestnicy rozmowy
              </p>
              <div className="flex flex-wrap gap-1">
                {c.interview_participants_names.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)]"
                  >
                    <UserIcon size={10} />
                    {p.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
              Checklist
            </p>
            <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <li className="flex items-center gap-2">
                {c.confirmation_email_sent ? (
                  <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle size={12} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                )}
                Mail potwierdzający spotkanie
              </li>
              <li className="flex items-center gap-2">
                {c.hr_notified ? (
                  <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle size={12} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                )}
                Info do kadr
              </li>
            </ul>
          </div>

          {(c.first_work_day || c.rate) && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Zatrudnienie
              </p>
              <div className="text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
                {c.first_work_day && (
                  <p>
                    Pierwszy dzień:{" "}
                    <strong className="font-semibold">{formatDate(c.first_work_day)}</strong>
                  </p>
                )}
                {c.rate && (
                  <p>
                    Stawka: <strong className="font-semibold">{c.rate}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {cvFiles.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                CV
              </p>
              <div className="flex flex-col gap-1">
                {cvFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded px-2 py-1.5">
                    <FileText size={12} className="text-[var(--color-primary)] flex-shrink-0" />
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-[var(--color-primary)] hover:underline flex-1 truncate">
                      {f.original_name}
                    </a>
                    <button onClick={() => deleteFile.mutate({ candidateId: c.id, fileId: f.id })}
                      className="p-0.5 text-gray-300 hover:text-red-500 flex-shrink-0" title="Usuń plik">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {taskFiles.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Pliki zadania
              </p>
              <div className="flex flex-col gap-1">
                {taskFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded px-2 py-1.5">
                    <FileText size={12} className="text-purple-400 flex-shrink-0" />
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-[var(--color-primary)] hover:underline flex-1 truncate">
                      {f.original_name}
                    </a>
                    <button onClick={() => deleteFile.mutate({ candidateId: c.id, fileId: f.id })}
                      className="p-0.5 text-gray-300 hover:text-red-500 flex-shrink-0" title="Usuń plik">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export function RecruitmentPage() {
  const { data: internalUsers = [] } = useInternalUsers();
  const user = useAuthStore((s) => s.user);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editCandidate, setEditCandidate] = useState<Candidate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Candidate | null>(null);
  // Client-side column sort. null → keep the server's default order.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);

  const params: Record<string, string> = {};
  if (statusFilter) params.status = statusFilter;
  if (search) params.search = search;

  const { data, isLoading } = useCandidates(
    Object.keys(params).length ? params : undefined
  );
  const candidates = data?.data ?? [];
  const counts = data?.meta.counts;

  // Cycle a header: unsorted → asc → desc → unsorted (restores server order).
  const toggleSort = (key: SortKey) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });

  const sortedCandidates = useMemo(() => {
    if (!sort) return candidates;
    return [...candidates].sort((a, b) => compareCandidates(a, b, sort.key, sort.dir));
  }, [candidates, sort]);

  // Today's interviews banner — explains the nav badge ("brand"/primary dot).
  // Mirrors the backend counter exactly: interview today AND current user is a
  // participant. Sourced from an unfiltered list so the banner stays accurate
  // regardless of the status/search filters above (shares cache when no filter).
  const { data: allCandidatesData } = useCandidates();
  const todayInterviews = useMemo(() => {
    const all = allCandidatesData?.data ?? [];
    const now = new Date();
    return all
      .filter((c) => {
        if (!c.interview_date) return false;
        if (user && !c.interview_participants.includes(user.id)) return false;
        return isSameLocalDay(new Date(c.interview_date), now);
      })
      .sort((a, b) =>
        (a.interview_date ?? "").localeCompare(b.interview_date ?? "")
      );
  }, [allCandidatesData, user]);

  const deleteCandidate = useDeleteCandidate();

  const openCreate = () => {
    setEditCandidate(null);
    setModalOpen(true);
  };

  const openEdit = (c: Candidate) => {
    setEditCandidate(c);
    setModalOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteCandidate.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5 sm:mb-6">
        <div className="min-w-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Rekrutacja</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3 sm:mb-6" />
          </div>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            Zarządzanie kandydatami — widoczne tylko dla dyrekcji
          </p>
        </div>
        <Button onClick={openCreate} className="w-full sm:w-auto justify-center">
          <UserPlus size={15} className="mr-1.5" /> Dodaj kandydata
        </Button>
      </div>

      {/* Today's interviews — matches the nav badge: today's interviews where the
          current user participates. Hidden when there are none. */}
      {todayInterviews.length > 0 && (
        <div className="mb-5 sm:mb-6 rounded-md border-l-4 border-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={16} className="text-[var(--color-primary)] flex-shrink-0" />
            <p className="text-sm font-semibold text-[var(--color-primary)]">
              {todayInterviews.length === 1
                ? "Masz dziś 1 rozmowę rekrutacyjną"
                : `Masz dziś ${todayInterviews.length} rozmów rekrutacyjnych`}
            </p>
          </div>
          <ul className="space-y-1">
            {todayInterviews.map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-700 dark:text-gray-200"
              >
                <span className="inline-flex items-center gap-1 font-semibold tabular-nums text-[var(--color-primary)]">
                  <Clock size={13} className="flex-shrink-0" />
                  {fmtTime(c.interview_date)}
                </span>
                <button
                  type="button"
                  onClick={() => openEdit(c)}
                  className="font-medium hover:underline"
                >
                  {c.first_name} {c.last_name}
                </button>
                {c.candidate_phone && (
                  <a
                    href={`tel:${c.candidate_phone}`}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)]"
                  >
                    {c.candidate_phone}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats */}
      {counts && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 mb-5 sm:mb-6">
          {(
            [
              { label: "Wszystkich", value: counts.total, color: "text-gray-700 dark:text-gray-200" },
              { label: "W trakcie", value: counts.in_process, color: "text-[var(--color-primary)] dark:text-blue-400" },
              { label: "Przyjętych", value: counts.accepted, color: "text-green-600 dark:text-green-400" },
              { label: "Odrzuconych", value: counts.rejected, color: "text-red-500 dark:text-red-400" },
              { label: "Wstrzymanych", value: counts.on_hold, color: "text-yellow-600 dark:text-yellow-400" },
            ] as const
          ).map((s) => (
            <div
              key={s.label}
              className="bg-white dark:bg-gray-800 px-2 sm:px-4 py-2 sm:py-3 text-center rounded-md sm:rounded-none"
            >
              <p className={`text-lg sm:text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <div className="relative flex-1 sm:flex-initial">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Szukaj po nazwisku..."
            className="pl-8 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] w-full sm:w-52"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] w-full sm:w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Wszystkie statusy</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {isLoading ? (
        <PageLoader />
      ) : candidates.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-10 text-center rounded-md">
          <UserPlus size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            {Object.keys(params).length
              ? "Brak wyników dla podanych filtrów."
              : "Brak kandydatów. Dodaj pierwszego!"}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col gap-2">
            {candidates.map((c) => (
              <CandidateCard
                key={c.id}
                c={c}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {(
                    [
                      { label: "Kandydat", cls: "px-4 py-3 text-left", sortKey: "candidate" },
                      { label: "Prowadzący", cls: "px-3 py-3 text-left", sortKey: "process_leader" },
                      { label: "Tel.", cls: "px-3 py-3 text-center", sortKey: "phone" },
                      { label: "Zadanie", cls: "px-3 py-3 text-center", sortKey: "task" },
                      { label: "Rozmowa", cls: "px-3 py-3 text-left", sortKey: "interview" },
                      { label: "Status", cls: "px-3 py-3 text-left", sortKey: "status" },
                      { label: "Pliki", cls: "px-3 py-3 text-left", sortKey: "files" },
                      { label: "", cls: "px-3 py-3", sortKey: null },
                    ] as { label: string; cls: string; sortKey: SortKey | null }[]
                  ).map((h) => {
                    const active = h.sortKey !== null && sort?.key === h.sortKey;
                    return (
                      <th
                        key={h.label}
                        className={`${h.cls} text-xs font-semibold uppercase tracking-wide ${
                          active
                            ? "text-gray-700 dark:text-gray-200"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        {h.sortKey ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(h.sortKey as SortKey)}
                            className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200 ${
                              h.cls.includes("text-center") ? "justify-center" : ""
                            }`}
                            title="Sortuj"
                          >
                            {h.label}
                            {active ? (
                              sort?.dir === "asc" ? (
                                <ChevronUp size={12} />
                              ) : (
                                <ChevronDown size={12} />
                              )
                            ) : (
                              <ChevronsUpDown size={12} className="opacity-40" />
                            )}
                          </button>
                        ) : (
                          h.label
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortedCandidates.map((c) => (
                  <CandidateRow
                    key={c.id}
                    c={c}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create / Edit modal */}
      <CandidateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editCandidate}
        users={internalUsers}
      />

      {/* Delete confirm */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Usuń kandydata"
        size="sm"
      >
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Czy na pewno chcesz usunąć{" "}
          <strong>
            {deleteTarget?.first_name} {deleteTarget?.last_name}
          </strong>
          ? Usunięte zostaną też wszystkie pliki kandydata.
        </p>
        <div className="flex gap-3">
          <Button
            variant="danger"
            loading={deleteCandidate.isPending}
            onClick={confirmDelete}
          >
            Usuń
          </Button>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Anuluj
          </Button>
        </div>
      </Modal>
    </div>
  );
}
